<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import { computed, onBeforeMount, ref } from 'vue'
import { RouteLocationRaw, useRoute } from 'vue-router'
import dayjs from 'dayjs'

import { useAuthStore } from '@/stores/auth-store'
import {
    uuid,
    PaginatedResponse,
    PermissionType,
    MinimalResource,
    MyDateRangePickerRef,
} from '@/types/general'
import { usePaginatedTable } from '@/hooks/table/use-paginated-table'
import { MinimalTour, TourState, OptimizationType } from '@/types/delivery-management'
import { GetDataParameters } from '@/types/table'
import { DropdownOption, DropdownStringOption } from '@/types/inputs'
import { useConfirm } from '@/hooks/use-confirm'
import { MyButtonScheme } from '@/types/layout/my-button'
import { datetime, differenceFromMinutes } from '@/utils/dates'
import { useLazyLoadedList } from '@/hooks/use-lazy-loaded-list'
import { numberFormatter } from '@/utils/numbers'
import { TourType } from '@/types/driver-report'
import { tourTypes } from '@/utils/type-translations'
import { useMittListener } from '@/hooks/use-mitt-listener'
import { TransportObjectType } from '@/types/transport-object'

import Breadcrumb from '@/components/layout/Breadcrumb.vue'
import CrumbsAndActions from '@/components/layout/CrumbsAndActions.vue'
import MyPanel from '@/components/my-components/MyPanel.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyTable from '@/components/table/MyTable.vue'
import MyTableColumn from '@/components/table/MyTableColumn.vue'
import ActionMenuItem from '@/components/table/ActionMenuItem.vue'
import MyFilterButton from '@/components/table/MyFilterButton.vue'
import MyFilterDivider from '@/components/table/MyFilterDivider.vue'
import MyFilterSelect from '@/components/table/MyFilterSelect.vue'
import ManageTourModal from '@/components/delivery-management/manage-tour/ManageTourModal.vue'
import ActionRowItem from '@/components/table/ActionRowItem.vue'
import CreateTicketModal from '@/components/delivery-management/CreateTicketModal.vue'
import TourDriverStatus from '@/components/delivery-management/TourDriverStatus.vue'
import MyDateRangePicker from '@/components/my-components/MyDateRangePicker.vue'
import ManageTourTransportObjectsModal from '@/components/delivery-management/tour-modal/ManageTourTransportObjectsModal.vue'
import ManageTourPlacementModal from '@/components/delivery-management/tour-modal/ManageTourPlacementModal.vue'

interface FetchToursParams extends Record<string, unknown>, GetDataParameters {
    filter: {
        type: TourType | undefined
        state: TourState | undefined
        'user-id': uuid | undefined
        'hub-ids': uuid | string | undefined
        'start-date': string | undefined
        'end-date': string | undefined
    }
}

const { t } = useI18n()
const authStore = useAuthStore()
const confirm = useConfirm()
const route = useRoute()

const manageTourTransportObjectsModalOpen = ref(false)
const manageTourPlacementModalOpen = ref(false)
const tourModalOpen = ref(false)
const ticketModalOpen = ref(false)
const selectedTourId = ref<uuid>()
const selectedTour = ref<MinimalTour>()
const dateRangePickerRef = ref<MyDateRangePickerRef>()
const drivers = useLazyLoadedList<DropdownStringOption>(fetchDrivers)
const hubs = useLazyLoadedList<DropdownStringOption>(fetchHubs)

const { data, paginationData, params, loading, error, refetch } = usePaginatedTable<
    MinimalTour,
    FetchToursParams
>(
    async (params, abortController) => {
        const response = await axios.get<PaginatedResponse<MinimalTour>>(
            window.route('dm.company.tours.index', {
                company: authStore.companyId,
            }),
            { params: params, signal: abortController.signal },
        )

        return response.data
    },
    {
        filter: {
            type: undefined,
            state: undefined,
            'user-id': undefined,
            'hub-ids': undefined,
            'start-date': dayjs().startOf('day').toDate().toISOString(),
            'end-date': dayjs().endOf('day').toDate().toISOString(),
        },
    },
)

const tourTypeOptions = computed<DropdownOption[]>(() => {
    return Object.entries(tourTypes).map(([value, translation]) => ({
        label: t(translation),
        value,
    }))
})

const tourDriverConfirmationEnabled = computed(() => {
    return authStore.deliveryManagementPreferences()?.tourDriverConfirmation ?? false
})

function openTransportObjectsModal(tourId: uuid) {
    selectedTourId.value = tourId
    manageTourTransportObjectsModalOpen.value = true
}

function openPlacementModal(tourId: uuid) {
    selectedTourId.value = tourId
    manageTourPlacementModalOpen.value = true
}

function canManageTour(tour: MinimalTour): boolean {
    if (tour.company.id === authStore.companyId) return true
    if (tour.shipperCompany?.id === authStore.companyId) return true

    if (!tour.haulierCanManage) return false
    if (tour.haulierCompany?.id === authStore.companyId) return true

    return !!tour.hub?.hauliers.map((haulier) => haulier.id).includes(authStore.companyId)
}

function earlyOrDelayedTooltip(row: MinimalTour): string {
    const early = `${row.handledEarlyShipmentsCount}/${row.shipmentsCount}`
    const delayed = `${row.delayedShipmentsCount}/${row.shipmentsCount - row.handledEarlyShipmentsCount}`

    return `${early} ${t('isEarly')}\n${delayed} ${t('isDelayed')}`
}

function slaPercentage(row: MinimalTour): string {
    if (!row.delayedShipmentsCount && !row.handledEarlyShipmentsCount) return '100%'
    const onTimeShipments =
        row.shipmentsCount - row.delayedShipmentsCount - row.handledEarlyShipmentsCount

    return numberFormatter.format((onTimeShipments / row.shipmentsCount) * 100) + '%'
}

async function fetchDrivers(): Promise<DropdownStringOption[]> {
    const response = await axios.get<MinimalResource[]>(
        window.route('minimal.companies.employees', { company: authStore.companyId }),
    )

    return response.data.map((driver) => ({
        value: driver.id,
        label: driver.name,
    }))
}

async function fetchHubs(): Promise<DropdownStringOption[]> {
    const response = await axios.get<MinimalResource[]>(
        window.route('minimal.companies.hubs', { company: authStore.companyId }),
    )

    return response.data.map((hub) => ({
        value: hub.id,
        label: hub.name,
    }))
}

function updateTour(tour: MinimalTour) {
    selectedTour.value = tour
    tourModalOpen.value = true
}

function createTicket(tour: MinimalTour) {
    selectedTour.value = tour
    ticketModalOpen.value = true
}

async function tourModalClosed() {
    tourModalOpen.value = false
    selectedTour.value = undefined
}

function setFilter(type: string | number | null | undefined, filterName: string) {
    params.value.filter[filterName] = type
    params.value.page = 1
}

function showTour(id: string): RouteLocationRaw {
    return {
        name: 'dm.tours.show',
        params: { tourId: id },
        query: route.query,
    }
}

function showShipments(id: string): RouteLocationRaw {
    let startDate = dayjs().year(2023).startOf('year').toDate().toISOString()
    if (params.value.filter['start-date']) {
        startDate = params.value.filter['start-date']
    }

    let endDate = dayjs().endOf('day').toDate().toISOString()
    if (params.value.filter['end-date']) {
        endDate = params.value.filter['end-date']
    }

    return {
        name: 'dm.shipments',
        query: {
            filter: {
                // @ts-ignore
                'tour-id': id,
                'start-date': startDate,
                'end-date': endDate,
            },
        },
    }
}

function getRegistrationNumbers(tour: MinimalTour, type: TransportObjectType): string {
    return tour.transportObjects
        .filter((o) => o.type === type)
        .map((o) => o.registrationNumber ?? '')
        .filter((r) => r)
        .join(', ')
}

async function deleteTour(tour: MinimalTour) {
    try {
        await confirm(
            t('deleteEntityTitle', { entity: t('tour') }),
            t('deleteEntityDescription', { entity: t('tour') }),
            {
                confirmText: t('yes'),
                cancelText: t('no'),
                confirmButtonScheme: MyButtonScheme.Warning,
            },
        )
    } catch {
        return
    }

    await axios.delete(
        window.route('dm.company.tours.destroy', {
            company: authStore.companyId,
            tour: tour.id,
        }),
    )
    await refetch()
}

onBeforeMount(() => {
    if (params.value.filter['user-id']) drivers.fetch()
    if (params.value.filter['hub-ids']) hubs.fetch()
})

useMittListener('fetchTours', () => refetch())
</script>

<template>
    <CrumbsAndActions>
        <Breadcrumb :to="{ name: 'dm' }" v-text="t('deliveryManagement')" />
        <Breadcrumb current v-text="t('tours')" />

        <template v-if="!authStore.isReadonly.deliveryManagement" #actions>
            <MyButton plain scheme="primary" size="small" @click="tourModalOpen = true">
                <mdi:plus-thick class="mr-1" />
                {{ t('addTour') }}
            </MyButton>
        </template>
    </CrumbsAndActions>

    <RouterView />

    <MyPanel v-if="authStore.companyId" shadow>
        <MyTable
            table-id="dm-tours"
            :rows="data"
            :pagination-data="paginationData"
            :loading="loading"
            :error="error"
            :get-data="refetch"
            :entity-name="t('tours')"
            :has-time-range="!!params.filter['start-date']"
        >
            <template #filters>
                <MyDateRangePicker
                    ref="dateRangePickerRef"
                    :filter="params.filter"
                    @start-date-changed="setFilter($event, 'start-date')"
                    @end-date-changed="setFilter($event, 'end-date')"
                />

                <MyFilterDivider />

                <MyFilterButton
                    filter-name="state"
                    value="created"
                    @selected="setFilter"
                    v-text="t('created')"
                />

                <MyFilterButton
                    filter-name="state"
                    value="enRoute"
                    @selected="setFilter"
                    v-text="t('enRoute')"
                />

                <MyFilterButton
                    filter-name="state"
                    value="finished"
                    @selected="setFilter"
                    v-text="t('finished')"
                />

                <MyFilterDivider />

                <MyFilterSelect
                    :label="t('type')"
                    :options="tourTypeOptions"
                    filter-name="type"
                    @change="setFilter"
                />

                <MyFilterSelect
                    :label="t('hubs')"
                    :options="hubs.items.value"
                    :loading="hubs.fetching.value"
                    filter-name="hub-ids"
                    translation-key="hubsSelected"
                    multiple
                    @focus="hubs.fetch()"
                    @change="setFilter"
                />

                <MyFilterSelect
                    :label="t('drivers')"
                    :options="drivers.items.value"
                    :loading="drivers.fetching.value"
                    filter-name="user-id"
                    @focus="drivers.fetch()"
                    @change="setFilter"
                />
            </template>

            <template #actionRow="{ row }">
                <RouterLink :to="showTour(row.id)">
                    <ActionRowItem>
                        <mdi:eye />
                    </ActionRowItem>
                </RouterLink>
            </template>

            <template
                v-if="
                    authStore.hasPermission(PermissionType.ManageCompany) &&
                    !authStore.isReadonly.deliveryManagement
                "
                #actionMenu="{ row }"
            >
                <RouterLink :to="showShipments(row.id)">
                    <ActionMenuItem v-tooltip="t('showShipmentsForTour')">
                        <span class="mr-2"><mdi:eye /></span>
                        {{ t('showShipments') }}
                    </ActionMenuItem>
                </RouterLink>

                <ActionMenuItem
                    v-if="row.company.id === authStore.companyId && row.shipperCompany"
                    @click="createTicket(row)"
                >
                    <span class="mr-2"><mdi:briefcase-edit /></span>
                    {{ t('createEntity', { entity: t('ticket') }) }}
                </ActionMenuItem>

                <ActionMenuItem
                    v-if="row.endedAt === null && canManageTour(row)"
                    @click="updateTour(row)"
                >
                    <span class="mr-2"><mdi:pencil /></span>
                    {{ t('edit') }}
                </ActionMenuItem>

                <ActionMenuItem @click="deleteTour(row)">
                    <span class="mr-2"><mdi:trash-can /></span>
                    {{ t('remove') }}
                </ActionMenuItem>
            </template>

            <MyTableColumn :name="t('hub')" property="hub.name" />

            <MyTableColumn :name="t('name')" property="name" />

            <MyTableColumn :name="t('plannedAt')" property="plannedAt" />
            <template #plannedAt="{ row }">
                <span v-text="datetime(row.plannedAt)" />
            </template>

            <MyTableColumn
                :name="t('estimatedDuration')"
                property="estimatedDuration"
                :sortable="false"
            />
            <template #estimatedDuration="{ row }">
                <span v-if="row.optimizationDetails?.duration" class="flex items-center">
                    <span
                        v-tooltip="t('hourMinuteFormatExplanation')"
                        v-text="differenceFromMinutes(row.optimizationDetails?.duration / 60)"
                    />
                    <mdi:lightning-bolt
                        v-if="row.optimizationDetails.type === OptimizationType.Optimized"
                        v-tooltip="t('optimizedHeadline')"
                        class="text-yellow-400 ml-1"
                    />
                    <mdi:timer-outline
                        v-if="row.optimizationDetails.type === OptimizationType.ETACalculated"
                        v-tooltip="t('etaCalculatedHeadline')"
                        class="text-green-400 ml-1"
                    />
                </span>
            </template>

            <MyTableColumn :name="t('endedAt')" property="endedAt" />
            <template #endedAt="{ row }">
                <span v-text="row.endedAt ? datetime(row.endedAt) : ''" />
            </template>

            <MyTableColumn :name="t('status')" property="state" :sortable="false" />
            <template #state="{ row }">
                <div v-if="row.endedAt === null && row.startedAt === null">
                    {{ t('created') }}
                </div>
                <div v-if="row.endedAt === null && row.startedAt !== null">
                    {{ t('enRoute') }}
                </div>
                <div v-if="row.endedAt !== null && row.startedAt !== null">
                    {{ t('finished') }}
                </div>
            </template>

            <MyTableColumn :name="t('company')" property="company.name" />

            <MyTableColumn :name="t('shipper')" property="shipperCompany.name" />

            <MyTableColumn :name="t('serviceCodes')" property="categories" :sortable="false" />

            <MyTableColumn :name="t('shipments')" property="shipmentsCount" />

            <MyTableColumn
                :name="t('shipmentsSummary')"
                property="shipmentsSummary"
                :sortable="false"
                hidden
            />
            <template #shipmentsSummary="{ row }">
                <div class="flex items-center gap-4">
                    <!-- Refrigerated -->
                    <div v-if="row.refrigeratedShipmentsCount > 0" class="flex items-center gap-1">
                        <span class="font-semibold">
                            {{ row.refrigeratedShipmentsCount }}
                        </span>
                        <mdi:thermometer-low
                            v-tooltip="t('refrigerated')"
                            class="h-4 w-4 text-blue-400"
                        />
                    </div>

                    <!-- Frozen -->
                    <div v-if="row.frozenShipmentsCount > 0" class="flex items-center gap-1">
                        <span class="font-semibold">
                            {{ row.frozenShipmentsCount }}
                        </span>
                        <mdi:snowflake-thermometer
                            v-tooltip="t('frozen')"
                            class="h-4 w-4 text-blue-400"
                        />
                    </div>

                    <!-- Chilled -->
                    <div v-if="row.chilledShipmentsCount > 0" class="flex items-center gap-1">
                        <span class="font-semibold">
                            {{ row.chilledShipmentsCount }}
                        </span>
                        <mdi:thermometer v-tooltip="t('chilled')" class="h-4 w-4 text-blue-400" />
                    </div>
                </div>
            </template>

            <MyTableColumn :name="t('deliveredShipments')" property="deliveredShipmentsCount" />

            <MyTableColumn :name="t('delivered')" property="percentage" :sortable="false" />
            <template #percentage="{ row }">
                <span v-if="row.shipmentsCount === 0">0%</span>

                <span v-else>
                    {{ Math.round((row.deliveredShipmentsCount / row.shipmentsCount) * 100) + '%' }}
                </span>
            </template>

            <MyTableColumn :name="t('sla')" property="sla" :sortable="false" />
            <template #sla="{ row }">
                <div v-tooltip="earlyOrDelayedTooltip(row)">
                    <span
                        :class="{
                            'text-yellow-600 font-semibold':
                                row.delayedShipmentsCount || row.handledEarlyShipmentsCount,
                        }"
                        v-text="slaPercentage(row)"
                    />
                </div>
            </template>

            <MyTableColumn :name="t('deviations')" property="deviationsCount" />
            <template #deviationsCount="{ row }">
                <div class="flex items-center space-x-2">
                    <span v-text="row.deviationsCount" />
                    <mdi:alert v-if="row.deviationsCount > 0" class="text-yellow-500" />
                </div>
            </template>
            <MyTableColumn :name="t('tickets')" property="ticketsCount" />
            <template #ticketsCount="{ row }">
                <div class="flex items-center space-x-2">
                    <span v-text="row.ticketsCount" />
                    <mdi:alert v-if="row.ticketsCount > 0" class="text-yellow-500" />
                </div>
            </template>

            <MyTableColumn :name="t('currentDriver')" property="currentDriver.name" hidden />
            <MyTableColumn :name="t('drivers')" property="drivers" hidden :sortable="false" />
            <template #drivers="{ row }">
                {{ row.drivers.map((driver) => driver.user.name).join(', ') }}
            </template>

            <MyTableColumn
                v-if="tourDriverConfirmationEnabled"
                :name="t('driverStatus')"
                property="driverStatus"
                :sortable="false"
                hidden
            />
            <template #driverStatus="{ row }">
                <div class="flex space-x-2">
                    <TourDriverStatus
                        v-for="driver in row.drivers"
                        :key="driver.id"
                        :driver="driver"
                    />
                </div>
            </template>

            <!-- Vehicles -->
            <MyTableColumn :name="t('vehicles')" property="vehicles" :sortable="false" />
            <template #vehicles="{ row }">
                <div class="group/item flex items-center">
                    <span class="w-full truncate">
                        {{ getRegistrationNumbers(row, TransportObjectType.Vehicle) }}
                    </span>
                    <span
                        class="cursor-pointer pl-3 text-stone-600 opacity-0 transition-opacity group-hover/item:opacity-100 dark:text-primary-50"
                        @click="openTransportObjectsModal(row.id)"
                    >
                        <mdi:pencil />
                    </span>
                </div>
            </template>

            <!-- Units -->
            <MyTableColumn :name="t('units')" property="units" :sortable="false" />
            <template #units="{ row }">
                <div class="group/item flex items-center">
                    <span class="w-full truncate">
                        {{ getRegistrationNumbers(row, TransportObjectType.Unit) }}
                    </span>
                    <span
                        class="cursor-pointer pl-3 text-stone-600 opacity-0 transition-opacity group-hover/item:opacity-100 dark:text-primary-50"
                        @click="openTransportObjectsModal(row.id)"
                    >
                        <mdi:pencil />
                    </span>
                </div>
            </template>
            <MyTableColumn :name="t('type')" property="type" hidden />
            <template #type="{ row }">
                <span v-if="row.type === TourType.Domestic" v-text="t('domestic')" />
                <span v-if="row.type === TourType.Export" v-text="t('export')" />
            </template>

            <MyTableColumn :name="t('haulier')" property="haulierCompany.name" hidden />
            <MyTableColumn :name="t('startedAt')" property="startedAt" hidden />
            <template #startedAt="{ row }">
                <span v-if="row.startedAt" v-text="datetime(row.startedAt)" />
            </template>

            <MyTableColumn :name="t('placement')" property="placement" hidden filterable />
            <template #placement="{ row }">
                <div class="group/item flex items-center">
                    <span class="w-full truncate" v-text="row.placement" />

                    <span
                        class="cursor-pointer pl-3 text-stone-600 opacity-0 transition-opacity group-hover/item:opacity-100 dark:text-primary-50"
                        @click="openPlacementModal(row.id)"
                    >
                        <mdi:pencil />
                    </span>
                </div>
            </template>

            <MyTableColumn
                :name="t('dispatcher')"
                property="dispatcherUser.name"
                hidden
                filterable
            />

            <template #noDataButton>
                <MyButton
                    v-if="params.filter['start-date']"
                    plain
                    scheme="light"
                    size="small"
                    @click="dateRangePickerRef?.increaseRange(6, 'months')"
                >
                    <mdi:magnify class="mr-2 h-4 w-4" />
                    {{ t('searchLast6Months') }}
                </MyButton>
            </template>
        </MyTable>
    </MyPanel>

    <ManageTourTransportObjectsModal
        v-if="manageTourTransportObjectsModalOpen && selectedTourId"
        v-model="manageTourTransportObjectsModalOpen"
        :tour-id="selectedTourId"
    />

    <ManageTourPlacementModal
        v-if="manageTourPlacementModalOpen && selectedTourId"
        v-model="manageTourPlacementModalOpen"
        :tour-id="selectedTourId"
    />

    <ManageTourModal
        v-if="tourModalOpen"
        v-model="tourModalOpen"
        :tour-id="selectedTour?.id"
        :is-hub-haulier="selectedTour ? canManageTour(selectedTour) : false"
        @close="tourModalClosed"
        @saved="refetch()"
    />

    <CreateTicketModal v-model="ticketModalOpen" :tour="selectedTour" />
</template>
